import type { StepView } from '~/helpers/learner/session-view'

export type LockReasonMessage = { key: string, values: Record<string, string | number> }

/**
 * Ce qu'on dit à l'apprenant d'une étape fermée : une clé i18n et ses valeurs.
 *
 * La raison est LUE (`lockReason`, `unlockAt`, prérequis), jamais recalculée :
 * le verrou ne s'évalue que côté API (`module-unlock.ts`, ADR 0002 § 3). Quatre
 * écrans l'affichaient chacun avec son ternaire ; les nouvelles raisons de #80
 * en auraient fait quatre de plus à tenir d'accord.
 *
 * Le module prérequis est cherché parmi les étapes du même parcours, où l'API
 * garantit qu'il figure ; s'il manque malgré tout, on dit « verrouillé » plutôt
 * que d'inventer une raison.
 */
export function lockReasonMessage(
  step: StepView,
  steps: StepView[],
  formatDate: (date: unknown) => string,
): LockReasonMessage {
  if (step.lockReason === 'AWAITING_DOCUMENTS') {
    return { key: 'home.path.awaitingDocuments', values: {} }
  }
  if (step.lockReason === 'AWAITING_POSITIONING') {
    return { key: 'home.path.awaitingPositioning', values: {} }
  }
  if (step.lockReason === 'SCHEDULED' && step.unlockAt) {
    return { key: 'home.path.opensOn', values: { date: formatDate(step.unlockAt) } }
  }
  if (step.lockReason === 'AWAITING_PREREQUISITE' || step.lockReason === 'AWAITING_SCORE') {
    const title = steps.find(other => other.module?.id === step.prerequisiteModuleId)?.module?.title
    if (!title) return { key: 'learnerSearch.locked', values: {} }
    return step.lockReason === 'AWAITING_SCORE'
      ? { key: 'home.path.awaitingScore', values: { title, score: Math.round((step.prerequisiteMinScore ?? 0) * 100) } }
      : { key: 'home.path.awaitingPrerequisite', values: { title } }
  }
  return { key: 'home.path.awaitingPrevious', values: {} }
}
